"use client";

import React from "react";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { Button, IconButton, useMediaQuery, Tooltip } from "@mui/material";
import { InsightsRounded as AnalyticsIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { canViewAnalytics } from "@/lib/admin/permissions";

/**
 * Header/sidebar button that opens the PostHog analytics dashboard.
 * Visible only to users with `analytics:view` permission
 * (SUPER_ADMIN + ADMIN by default). Hidden while impersonating.
 */
export default function AdminAnalyticsButton() {
  const { data: session } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isActive = pathname === "/admin/analytics";

  if (!canViewAnalytics(session) || session?.impersonating) return null;

  const navigate = () => {
    const event = new CustomEvent("meetingNavigationRequest", {
      detail: { href: "/admin/analytics" },
      cancelable: true,
      bubbles: false,
    });
    if (document.dispatchEvent(event)) {
      router.push("/admin/analytics");
    }
  };

  return (
    <>
      {isMobile ? (
        <Tooltip title="Analytics">
          <IconButton
            color="inherit"
            onClick={navigate}
            size="small"
            sx={{ ml: 1 }}
          >
            <AnalyticsIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="text"
          color="inherit"
          onClick={navigate}
          startIcon={<AnalyticsIcon />}
          sx={{
            mr: 2,
            color: theme.palette.text.secondary,
            fontWeight: isActive ? 700 : 400,
            maxWidth: 200,
          }}
        >
          Analytics
        </Button>
      )}
    </>
  );
}
